import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { logout as logoutRequest, refreshSession } from '../hooks/auth';
import CustomerIdService from '../services/customer-id.service';
import EnvironmentIdService from '../services/environment-id.service';
import { setSessionExpiredHandler } from '../services/api';
import { clearAccessToken, setAccessToken } from '../services/accessToken';

export const AuthStatus = {
  loading: 'loading',
  authenticated: 'authenticated',
  unauthenticated: 'unauthenticated',
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState(AuthStatus.loading);
  const [user, setUser] = useState(null);

  const login = useCallback(response => {
    if (!response?.accessToken) return;

    setAccessToken(response.accessToken);
    setUser(response.user ?? null);
    setStatus(AuthStatus.authenticated);
  }, []);

  const endSession = useCallback(() => {
    clearAccessToken();
    CustomerIdService.removeId();
    EnvironmentIdService.removeId();
    setUser(null);
    setStatus(AuthStatus.unauthenticated);
    queryClient.clear();
  }, [queryClient]);

  // end session on frontend but also through backend
  const logout = useCallback(async () => {
    try {
      await logoutRequest();
    } catch {}

    endSession();
  }, [endSession]);

  // pass end session to api.js so it can be used in the interceptor
  useEffect(() => setSessionExpiredHandler(endSession), [endSession]);

  // on reload, we don't have the access token (and refresh is in cookie), so we need to check if the user is still logged in
  useEffect(() => {
    let cancelled = false;

    refreshSession()
      .then(response => {
        if (cancelled) return;
        if (response?.accessToken) {
          login(response);
        } else {
          setStatus(AuthStatus.unauthenticated);
        }
      })
      .catch(() => {
        if (cancelled) return;
        clearAccessToken();
        setStatus(AuthStatus.unauthenticated);
      });

    return () => {
      cancelled = true;
    };
  }, [login]);

  return (
    <AuthContext.Provider
      value={{
        status,
        isLoading: status === AuthStatus.loading,
        isAuthenticated: status === AuthStatus.authenticated,
        user,
        login,
        logout,
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
